import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { type DepartmentUpdate, type Department } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormUpdateDepartmentProps extends FormUpdateProps<Department> {}

import { store } from "../../store";

@observer
export class FormUpdateDepartment extends Component<FormUpdateDepartmentProps> {
    private validationId = createUuid();

    constructor(props: FormUpdateDepartmentProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryDepartments.validation.update.updateModel(
            this.validationId,
            this.departmentUpdate,
        );
    }

    componentDidMount(): void {
        store.repositoryDepartments.validation.update.initializeModel(
            this.validationId,
            this.departmentUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryDepartments.discardMutableCopy(FormUpdateDepartment.name, this.props.id);
    }

    @computed private get department(): Department | undefined {
        return store.repositoryDepartments.mutableCopyById(
            FormUpdateDepartment.name,
            this.props.id,
        );
    }

    @computed private get departmentUpdate(): DepartmentUpdate {
        return pick(["label"], this.department);
    }

    @action.bound
    private async updateDepartment(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const department = await store.repositoryDepartments.update(
            this.props.id,
            this.departmentUpdate,
        );
        if (this.props.onUpdate) {
            this.props.onUpdate(department);
        }
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateDepartment.submit.text")}
                validation={store.repositoryDepartments.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateDepartment}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formUpdateDepartment.label.label")}
                        disabled={!this.department}
                        {...doubleBindString(this.department!, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryDepartments.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateDepartment.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
